<template>
  <div v-if="totalPages > 1" class="tw-flex tw-items-center tw-justify-center tw-gap-x-4 tw-mt-8">
    <!-- First Page -->
    <QBtn
      flat
      round
      size="sm"
      color="grey"
      :disable="page === 1"
      icon="keyboard_double_arrow_left"
      @click="goToPage(1)"
    />

    <!-- Previous Page -->
    <QBtn
      flat
      round
      size="sm"
      color="grey"
      :disable="page === 1"
      icon="chevron_left"
      @click="goToPage(page - 1)"
    />

    <!-- Page Numbers -->
    <div class="tw-flex tw-gap-3">
      <QBtn
        v-for="pageNum in displayedPages"
        :key="pageNum"
        no-caps
        :label="pageNum"
        square
        outline
        class="tw-w-10 tw-h-10"
        :color="pageNum === page ? 'primary' : 'grey'"
        :text-color="pageNum === page ? 'primary' : 'grey'"
        @click="goToPage(pageNum)"
      />
    </div>

    <!-- Next Page -->
    <QBtn
      flat
      round
      size="sm"
      color="grey"
      :disable="page === totalPages"
      icon="chevron_right"
      @click="goToPage(page + 1)"
    />

    <!-- Last Page -->
    <QBtn
      flat
      round
      size="sm"
      color="grey"
      :disable="page === totalPages"
      icon="keyboard_double_arrow_right"
      @click="goToPage(totalPages)"
    />
  </div>
</template>

<script setup lang="ts">

const props = defineProps({
  align: {
    type: String,
    default: 'center',
  },
  total: {
    type: Number,
    required: true,
  },
  perPage: {
    type: Number,
    required: true,
  },
})

const totalPages = computed(() => Math.ceil(props.total / props.perPage))

// Use defineModel for two-way binding
const page = defineModel<number>('modelValue', { default: 1 })

// Function to navigate to a specific page
const goToPage = (newPage: number | string) => {
  if (typeof newPage === 'string' && newPage === '...') { return }
  page.value = Number(newPage)
}

// Calculate displayed page numbers
const displayedPages = computed(() => {
  const delta = 1 // Number of pages to show before and after current page
  const range = []
  const rangeWithDots = []
  let l

  for (let i = 1; i <= totalPages.value; i++) {
    if (
      i === 1 ||
      i === totalPages.value ||
      (i >= page.value - delta && i <= page.value + delta)
    ) {
      range.push(i)
    }
  }

  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1)
      } else if (i - l !== 1) {
        rangeWithDots.push('...')
      }
    }
    rangeWithDots.push(i)
    l = i
  }

  return rangeWithDots
})
</script>

<!-- No custom styles needed as we're using Quasar QBtn with Tailwind classes -->
